import { Table } from '@amzn/awsui-components-core';
import { Box, Button } from '@amzn/awsui-components-core';
import { Epic, Task } from '@amzn/awscat-pattern-library-service-model';
import React, { ReactNode } from 'react';
import { reorderById } from '../../../../common/Arrays';
import constants from '../../../../common/constants';
import '../../../../resources/styles/scss/components/common/TasksList.css';
import TaskEditorForm from './TaskEditorForm';
import { getSkillLabels } from '../../../utils/TaskUtils';
import ReadonlyEditorView from './ReadonlyEditorView';
import { MoveVerticallyHandle } from '../../MoveVerticallyHandle';
import { SortableList, SortableRenderArgs } from '../../../v2-common/SortableList';

// tslint:disable-next-line:no-namespace
declare module 'react' {
  namespace JSX {
    interface IntrinsicElements {
      'awsui-table': any;
    }
  }
}

// Stable across renders so SortableList's memoized id list stays valid.
const keyOfTask = (task: Task) => task.id;

interface TaskRowProps {
  readonly value: Task;
  readonly setNodeRef: SortableRenderArgs['setNodeRef'];
  readonly style: SortableRenderArgs['style'];
  readonly onTaskRemoved: (task: Task) => void;
  readonly onEditClick: (task: Task) => void;
}

// One editable task row; the drag handle binds to the enclosing SortableList item.
const TaskRow = ({ value, setNodeRef, style, onTaskRemoved, onEditClick }: TaskRowProps) => {
  const handleEditClick = () => onEditClick(value);
  const handleRemoveClick = () => {
    if (confirm(constants.TASK_REMOVE_CONFIRM_TEXT)) {
      onTaskRemoved(value);
    }
  };

  const title = <ReadonlyEditorView value={value.title} />;

  const description = <ReadonlyEditorView value={value.description} />;

  const skillLabels = getSkillLabels(value.skills);
  const skills = <ReadonlyEditorView value={skillLabels} htmlView />;

  return (
    <tr ref={setNodeRef} style={style} className="awsui-table-row task-list-table-row ">
      <td>
        <span>
          <span>{title}</span>
        </span>
      </td>
      <td>
        <span>
          <span>{description}</span>
        </span>
      </td>
      <td>
        <span>
          <span>{skills}</span>
        </span>
      </td>
      <td className="empty-column-width">
        <Box float={'right'}>
          <div className="edit-task-icon">
            <span className="cursor-pointer">
              <svg
                onClick={handleEditClick}
                width="20"
                height="20"
                viewBox="0 0 2048 2048"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path fill="#444" d={constants.EDIT_ICON_URI} />
              </svg>
            </span>
          </div>
          <div className="delete-task-icon">
            <Button
              className="cursor-pointer"
              variant="icon"
              iconUrl={'/images/icon-delete.svg'}
              iconAlt="Delete task"
              ariaLabel="Delete task"
              onClick={handleRemoveClick}
            />
          </div>
          <MoveVerticallyHandle />
        </Box>
      </td>
    </tr>
  );
};

// Wraps the sortable rows in the raw awsui-table markup; Cloudscape Table can't reorder rows.
const renderTaskTable = (rows: ReactNode) => {
  return (
    <awsui-table initialized="true">
      <div className="task-list-table awsui-table-inner awsui-table-variant-default">
        <div className="awsui-table-container">
          <table>
            <thead>
              <tr>
                <th>
                  <span className="awsui-table-header-content">
                    <span>
                      <span>Story</span>
                    </span>
                  </span>
                </th>
                <th>
                  <span className="awsui-table-header-content">
                    <span>
                      <span>Description</span>
                    </span>
                  </span>
                </th>
                <th>
                  <span className="awsui-table-header-content">
                    <span>
                      <span data-reactroot="">Skills required</span>
                    </span>
                  </span>
                </th>
                <th>
                  <span className="awsui-table-header-content" />
                </th>
              </tr>
            </thead>
            <tbody>{rows}</tbody>
          </table>
        </div>
      </div>
    </awsui-table>
  );
};

interface Props {
  epics: Epic[];
  tasks: Task[];
  onTasksChanged: (tasks: Task[]) => void;
  isEditable: boolean;
}

interface State {
  currentEditingTask: Task;
  isEditableTask: boolean;
}

export default class TasksList extends React.Component<Props, State> {
  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.state = {
      currentEditingTask: new Task(),
      isEditableTask: false,
    };
  }

  render() {
    const { tasks, isEditable, epics } = this.props;
    const { isEditableTask, currentEditingTask } = this.state;
    if (isEditable) {
      const taskList =
        Array.isArray(tasks) && tasks.length ? (
          <SortableList
            items={tasks}
            keyExtractor={keyOfTask}
            onReorder={this.onReorder}
            container={renderTaskTable}
            item={this.renderTaskRow}
          />
        ) : null;
      return (
        <div>
          {taskList}
          {this.renderEditForm(isEditableTask, currentEditingTask, epics, tasks)}
        </div>
      );
    } else {
      return <div>{this.renderTaskList.call(this, tasks)}</div>;
    }
  }

  // @ts-expect-error TS7006 - strict migration
  private renderEditForm = (isEditableTask, currentEditingTask, epics, tasks) => {
    if (isEditableTask) {
      return (
        <div>
          <TaskEditorForm
            epics={epics}
            editTask={this.onTaskEdited}
            currentEditingTask={currentEditingTask}
            onDismissClick={this.onDismissClick}
            tasks={tasks}
          />
        </div>
      );
    }
  };

  private onTaskEdited = (editedTask: any) => {
    const { tasks, onTasksChanged } = this.props;
    if (onTasksChanged) {
      onTasksChanged(
        tasks.map((task) => {
          return task.id !== editedTask.id ? task : editedTask;
        }),
      );
    }
  };

  private renderTaskList = (tasks: any[]) => {
    const columnDefinitions = [
      {
        // @ts-expect-error TS7006 - strict migration
        cell: (item) => <ReadonlyEditorView value={item.title} id={`${item.id}-story-title`} />,
        header: 'Story',
        id: 'Story',
      },
      {
        // @ts-expect-error TS7006 - strict migration
        cell: (item) => <ReadonlyEditorView value={item.description} id={item.id} />,
        header: 'Description',
        id: 'Description',
      },
      {
        // @ts-expect-error TS7006 - strict migration
        cell: (item) => <ReadonlyEditorView value={getSkillLabels(item.skills)} htmlView />,
        header: 'Skills required',
        id: 'Skills',
      },
    ];

    return (
      <Table
        columnDefinitions={columnDefinitions}
        items={tasks}
        empty={
          <Box textAlign="center" variant="p" margin={{ bottom: 's' }}>
            {constants.EMPTY_TASK_LIST}
          </Box>
        }
      />
    );
  };

  private renderTaskRow = (task: Task, { setNodeRef, style }: SortableRenderArgs) => (
    <TaskRow
      value={task}
      setNodeRef={setNodeRef}
      style={style}
      onTaskRemoved={this.onTaskRemoved}
      onEditClick={this.onEditClick}
    />
  );

  private onReorder = (orderedIds: readonly string[]) => {
    const { tasks, onTasksChanged } = this.props;
    if (onTasksChanged) {
      onTasksChanged(reorderById(tasks, orderedIds));
    }
  };

  private onEditClick = (item: any) => {
    this.setState({
      currentEditingTask: item,
      isEditableTask: true,
    });
  };

  private onDismissClick = () => {
    this.setState({
      currentEditingTask: new Task(),
      isEditableTask: false,
    });
  };

  private onTaskRemoved = (item: any) => {
    const { tasks, onTasksChanged } = this.props;
    if (onTasksChanged) {
      onTasksChanged(tasks.filter((task) => task.title !== item.title));
    }
  };
}
